import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	PetitionDetailsDiscussionData,
	type PetitionDetailsDiscussionDataQuery,
	type PetitionDetailsDiscussionDataQueryVariables,
} from './discussionData.graphql';

export type DiscussionData = NonNullable<PetitionDetailsDiscussionDataQuery['petition']>;

// TODO handle errors in the UI instead of falling back to empty data — see CHANGE-95697
const EMPTY_DISCUSSION_DATA: DiscussionData = {
	commentsConnection: {
		totalCount: 0,
		totalCountLocalized: '0',
		nodes: [],
	},
	petitionMediaHitsConnection: {
		totalCount: 0,
	},
	mediaHitsConnection: {
		nodes: [],
		pageInfo: { hasNextPage: false },
		totalCount: 0,
	},
	mediaSourcesConnection: {
		nodes: [],
		totalCount: 0,
		totalCountLocalized: '0',
	},
};

/**
 * Comments, media hits, and media sources are all backed by discussion_service. Fetching them as
 * their own request (instead of as part of the main PetitionDetailsData query) means a
 * discussion_service outage only degrades this data to empty defaults, instead of failing the
 * whole page (CHANGE-95607) or changing the error-handling contract of these fields for their
 * other GraphQL consumers (petitionFeed, signedPetitionHome, starter dashboard, etc.).
 */
export async function getPetitionDiscussionData(
	utilityContext: UtilityContext,
	variables: PetitionDetailsDiscussionDataQueryVariables,
): Promise<DiscussionData> {
	try {
		const { data } = await utilityContext.gql.fetch<
			PetitionDetailsDiscussionDataQuery,
			PetitionDetailsDiscussionDataQueryVariables
		>({
			query: PetitionDetailsDiscussionData,
			variables,
			rejectOnError: true,
			batched: true,
		});

		return data.petition ?? EMPTY_DISCUSSION_DATA;
	} catch {
		return EMPTY_DISCUSSION_DATA;
	}
}
